import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, FileSpreadsheet, Loader2 } from 'lucide-react'
import type { ServerApiClient } from '../../../data/ServerApiClient'

export type CfoWorkbookDownload = {
  busy: boolean
  disabled: boolean
  title: string
  label: string
  status: string
  error: boolean
  downloaded: boolean
  download: () => Promise<void>
}

function shortReason(error: unknown): string {
  const failure = (error || {}) as { status?: number; code?: string; name?: string }
  if (failure.status === 401) return 'Your session expired. Sign in again, then retry.'
  if (failure.status && failure.status >= 500) return 'Something went wrong on our side.'
  if (failure.name === 'AbortError') return 'The build took too long.'
  // Server bodies and exception messages are internal diagnostics, not UI copy.
  return 'Something went wrong on our side.'
}

export function unsupportedProviderTitle(provider: string): string {
  const name = ({ xero: 'Xero', sage: 'Sage', netsuite: 'NetSuite' } as Record<string, string>)[provider] || 'This provider'
  return `The CFO workbook download is available for QuickBooks companies today; ${name} is coming next. Open a blank workbook in Excel and use the FuseGrid add-in to build it now.`
}

export function useCfoWorkbookDownload({ appApi, workbookId, company, provider, bootstrapLoading }: {
  appApi: ServerApiClient; workbookId: string | null; company: string; provider: string | null; bootstrapLoading: boolean
}): CfoWorkbookDownload {
  const [ready, setReady] = useState(false)
  const [readinessLoading, setReadinessLoading] = useState(true)
  const [phase, setPhase] = useState<'idle' | 'building' | 'slow' | 'downloaded'>('idle')
  const [notice, setNotice] = useState({ text: '', error: false })
  const request = useRef<AbortController | null>(null)
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const clearTimers = useCallback(() => { timers.current.forEach(clearTimeout); timers.current = [] }, [])
  useEffect(() => {
    let cancelled = false
    request.current?.abort(); request.current = null
    clearTimers(); setPhase('idle'); setNotice({ text: '', error: false }); setReady(false)
    setReadinessLoading(Boolean(workbookId && provider === 'quickbooks'))
    if (workbookId && provider === 'quickbooks') void Promise.resolve().then(() => appApi.getCfoWorkbookReadiness(workbookId))
      .then((result) => { if (!cancelled) setReady(Boolean(result.latest_closed)) })
      .catch(() => { if (!cancelled) setReady(false) })
      .finally(() => { if (!cancelled) setReadinessLoading(false) })
    return () => { cancelled = true; request.current?.abort(); clearTimers() }
  }, [appApi, clearTimers, workbookId, provider])
  const loading = bootstrapLoading || readinessLoading
  const disabled = loading || provider !== 'quickbooks' || !ready
  const download = useCallback(async () => {
    if (!workbookId || disabled || request.current) return
    const controller = new AbortController()
    request.current = controller
    clearTimers(); setPhase('building'); setNotice({ text: '', error: false })
    const slow = setTimeout(() => setPhase('slow'), 10_000)
    const timeout = setTimeout(() => controller.abort(), 90_000)
    timers.current.push(slow, timeout)
    try {
      const { blob, fileName } = await appApi.downloadCfoWorkbook(workbookId, controller.signal)
      if (request.current !== controller) return
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url; anchor.download = fileName
      document.body.appendChild(anchor); anchor.click(); anchor.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setPhase('downloaded'); setNotice({ text: `Downloaded ${fileName}.`, error: false })
      timers.current.push(setTimeout(() => setPhase('idle'), 4000))
      timers.current.push(setTimeout(() => setNotice({ text: '', error: false }), 6000))
    } catch (error) {
      if (request.current !== controller) return
      const reason = shortReason(error)
      setPhase('idle'); setNotice({ text: `Couldn't build the workbook.${reason ? ` ${reason}` : ''}`, error: true })
    } finally {
      clearTimeout(slow); clearTimeout(timeout)
      if (request.current === controller) request.current = null
    }
  }, [appApi, clearTimers, disabled, workbookId])
  return {
    busy: phase === 'building' || phase === 'slow', disabled,
    title: loading ? 'Loading company…' : provider !== 'quickbooks' ? unsupportedProviderTitle(provider || '') : !ready ? 'No closed month yet: the workbook needs at least one closed month.'
      : `The full CFO workbook for ${company} as one Excel file: every sheet, styled and governed. Open it with the FuseGrid add-in to refresh.`,
    label: phase === 'building' ? 'Building workbook…' : phase === 'slow' ? 'Still building…' : phase === 'downloaded' ? 'Downloaded' : 'Download CFO workbook',
    status: notice.text, error: notice.error, downloaded: phase === 'downloaded', download,
  }
}

export function CfoWorkbookDownloadButton({ action }: { action: CfoWorkbookDownload }) {
  const Icon = action.busy ? Loader2 : action.downloaded ? Check : FileSpreadsheet
  return <div>
    <span title={action.title} style={{ display: 'inline-flex' }}>
      <button type="button" className="btn" title={action.title} disabled={action.disabled || action.busy} style={action.disabled || action.busy ? { pointerEvents: 'none' } : undefined} onClick={() => void action.download()}>
        <Icon size={15} className={action.busy ? 'spin' : undefined} aria-hidden="true" />{action.label}
      </button>
    </span>
    {action.status ? <p className={`cfo-statement-export-status${action.error ? ' error' : ''}`} role="status">
      {action.status}{action.error ? <> <button type="button" className="cfo-studio-linkbtn" onClick={() => void action.download()}>Try again.</button></> : null}
    </p> : null}
  </div>
}
